import React, { Fragment, PureComponent } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import every from 'lodash/every';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import classnames from 'classnames';
import qs from 'qs';
import { format as currencyFormat } from 'currency-formatter';
import BSDatepicker from '../BSDatepicker/BSDatepicker';
import CreditCardLogos from '../CreditCardLogos';
import CreditCardFields from '../CreditCardFields';
import * as analytics from '../../services/analytics';
import './Passport360EnrollmentCheckoutView.css';
import { createStripeTokenFromElement } from '../../services/utils';

const propTypes = {
  plans: PropTypes.array,
  plansFilter: PropTypes.object,
  updateQuote: PropTypes.func,
  checkoutQuote: PropTypes.func,
  onNotifyError: PropTypes.func,
  quoteCheckout: PropTypes.object,
};

class Passport360EnrollmentCheckoutView extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      isAcknowledgmentAgreed: false,
      isAutoRenewSelected: false,
      billingType: 'platform_eft',
      finalizeMethod: null,
      isInProgress: false,
    };

    const query = qs.parse(props.location.search, { ignoreQueryPrefix: true });
    this.isReadyToFinalize = !!query.finalize;
  }

  componentDidMount() {
    const { quote, updateQuote, checkoutQuote, onNotifyError } = this.props;
    analytics.page('Passport 360 - Checkout');

    if (this.isReadyToFinalize) {
      checkoutQuote(quote.quote_number).catch(onNotifyError);
    } else {
      updateQuote({
        ...quote,
        application: {
          policy_effective_date: moment().toISOString(),
          ...quote.application,
          plan_id: this.selectedPlan.id,
        },
      })
        .then(() => checkoutQuote(quote.quote_number))
        .catch(onNotifyError);
    }
  }

  handleAcknowledgmentUpdate = (e) => {
    analytics.track('Passport 360 - Finalize Acknowledgement Changed', {
      value: e.target.checked ? 1 : 0,
    });
    this.setState({
      isAcknowledgmentAgreed: e.target.checked,
    });
  };

  handleAutoRenewUpdate = (e) => {
    analytics.track('Passport 360 - Autorenew Changed', {
      value: e.target.checked ? 1 : 0,
    });
    this.setState({
      isAutoRenewSelected: e.target.checked,
    });
  };

  handleBillingTypeChange = (e) => {
    this.setState({
      billingType: e.target.value,
    });
  };

  handleCCFieldChange = (e) => {
    this.setState({
      isCardComplete: e.isComplete,
    });
  };

  handleDateChange = (e) => {
    analytics.track('Passport 360 - Start Date Updated');
    const { quote, updateQuote, checkoutQuote, onNotifyError } = this.props;
    updateQuote({
      ...quote,
      application: {
        ...quote.application,
        policy_effective_date: moment(e.date).toISOString(),
      },
    })
      .then(() => checkoutQuote(quote.quote_number))
      .catch(onNotifyError);
  };

  handleUpdateFinalizeMethod = (e) => {
    analytics.track('Passport 360 - Finalize Method Updated', {
      method: e.target.value,
    });
    this.setState({
      finalizeMethod: e.target.value,
    });
  };

  handleSubmitClient = (e) => {
    e.preventDefault();
    const { billingType, isAutoRenewSelected } = this.state;
    const {
      finalizeQuote,
      history,
      onNotifyError,
      quote,
      updateQuote,
      isShareLinkFlow,
    } = this.props;
    const determineBillingPromise = new Promise((resolve, reject) => {
      if (billingType === 'platform_eft') {
        createStripeTokenFromElement(
          this.creditCardFieldNode.element,
          this.creditCardFieldNode.cardName
        ).then((stripeToken) => {
          resolve({
            billing_type: 'platform_eft',
            source_token: stripeToken,
          });
        }, reject);
      } else {
        resolve({
          billing_type: 'carrier_bill',
        });
      }
    });

    this.setState({ isInProgress: true });
    determineBillingPromise
      .then((billingObj) => {
        return updateQuote({
          ...quote,
          application: {
            ...quote.application,
            billing: billingObj,
            auto_renewal: isAutoRenewSelected,
          },
        });
      })
      .then(() => {
        return finalizeQuote({
          quoteId: quote.quote_number,
          deleteAuthHeader: isShareLinkFlow,
        });
      })
      .then(() => {
        history.push('confirmation');
      })
      .catch((error) => {
        this.setState({ isInProgress: false });
        onNotifyError(error);
      });
  };

  handleSubmitBroker = (e) => {
    e.preventDefault();
    const {
      client,
      quote,
      finalizeQuote,
      handoffQuote,
      history,
      match,
      onNotifyError,
      onNotifySuccess,
      updateQuote,
    } = this.props;
    analytics.track('Passport 360 - Checkout Submitted', {
      method: this.state.finalizeMethod,
    });

    this.setState({ isInProgress: true });
    if (this.state.finalizeMethod === 'client_email') {
      handoffQuote({
        quoteNumber: quote.quote_number,
        handoffType: 'proposal',
      })
        .then(() => {
          analytics.track('Passport 360 - Checkout Submitted', {
            method: this.state.finalizeMethod,
          });
          onNotifySuccess(
            `<strong>${client.person.first_name} ${client.person.last_name}</strong> has been sent a quote proposal email.`
          );
          history.push(`/dashboard/clients/${match.params.clientId}`);
        })
        .catch((error) => {
          analytics.track('Passport 360 - Checkout Failed', {
            method: this.state.finalizeMethod,
          });
          this.setState({ isInProgress: false });
          onNotifyError(error);
        });
    } else {
      updateQuote({
        ...quote,
        application: {
          ...quote.application,
          billing: {
            billing_type: 'carrier_bill',
          },
        },
      })
        .then(() => {
          return finalizeQuote({ quoteId: quote.quote_number });
        })
        .then(() => {
          analytics.track('Passport 360 - Checkout Submitted', {
            method: this.state.finalizeMethod,
          });
          history.push('confirmation');
        })
        .catch((error) => {
          analytics.track('Passport 360 - Checkout Failed', {
            method: this.state.finalizeMethod,
          });
          this.setState({ isInProgress: false });
          onNotifyError(error);
        });
    }
  };

  handleSubmit = (e) => {
    if (this.props.actor === 'broker') {
      this.handleSubmitBroker(e);
    } else {
      this.handleSubmitClient(e);
    }
  };

  trackDocClick = (e) => {
    analytics.track('Broker Portal - External Link Clicked', {
      label: e.target.dataset.key,
    });
  };

  get isSubmitDisabled() {
    const { actor, isCheckingOutQuote } = this.props;
    const {
      billingType,
      finalizeMethod,
      isAcknowledgmentAgreed,
      isCardComplete,
      isInProgress,
    } = this.state;
    if (isInProgress) {
      return true;
    } else if (actor === 'broker') {
      if (finalizeMethod === 'by_broker') {
        return !isAcknowledgmentAgreed || isCheckingOutQuote;
      } else {
        return !finalizeMethod || isCheckingOutQuote;
      }
    } else {
      if (billingType === 'platform_eft') {
        return !isAcknowledgmentAgreed || !isCardComplete || isCheckingOutQuote;
      } else {
        return !isAcknowledgmentAgreed || isCheckingOutQuote;
      }
    }
  }

  get selectedPlan() {
    const { plans, plansFilter, quote } = this.props;
    if (!isEmpty(plansFilter)) {
      return find(plans, (plan) => {
        const details = plan.settings.details;
        const addOns = details.optional_coverages;
        return every([
          details.plan_type === plansFilter.planType,
          details.plan_name === plansFilter.planName,
          !isEmpty(addOns.golf_package) === !!plansFilter.golfPackage,
          !isEmpty(addOns.trip_cancellation) === !!plansFilter.tripCancellation,
        ]);
      });
    } else {
      return find(plans, { id: get(quote, 'application.plan_id') });
    }
  }

  get formNodeBroker() {
    const { quote } = this.props;
    const { finalizeMethod, isAcknowledgmentAgreed, isInProgress } = this.state;
    return (
      <Fragment>
        <div className="form-group">
          <label className="text-uppercase">Method of confirmation</label>
          <div className="radio radio-success">
            <input
              id="radioClientEmail"
              type="radio"
              name="finalizeMethod"
              value="client_email"
              checked={finalizeMethod === 'client_email'}
              onChange={this.handleUpdateFinalizeMethod}
            />
            <label
              className="Passport360EnrollmentCheckoutView-check"
              htmlFor="radioClientEmail"
            >
              <strong className="Passport360EnrollmentCheckoutView-check-label">
                Send proposal to the client
              </strong>
              <p className="Passport360EnrollmentCheckoutView-check-info">
                Your client will be sent an email with a link to the completed
                quote. They will review the plan and complete enrollment on
                their own.
              </p>
            </label>

            <input
              id="radioByBroker"
              type="radio"
              name="finalizeMethod"
              value="by_broker"
              checked={finalizeMethod === 'by_broker'}
              onChange={this.handleUpdateFinalizeMethod}
            />
            <label
              className="Passport360EnrollmentCheckoutView-check"
              htmlFor="radioByBroker"
            >
              <strong className="Passport360EnrollmentCheckoutView-check-label">
                Bind coverage on behalf of client
              </strong>
              <p className="Passport360EnrollmentCheckoutView-check-info">
                You will manually complete the enrollment and a policy binder
                and invoice will be emailed to your client.
              </p>
            </label>
          </div>

          {finalizeMethod === 'by_broker' && (
            <div className="checkbox check-success">
              <input
                id="checkboxAcknowledgment"
                type="checkbox"
                name="acknowledgment"
                value="acknowledgment"
                checked={isAcknowledgmentAgreed || false}
                onChange={this.handleAcknowledgmentUpdate}
              />
              <label
                className="Passport360EnrollmentCheckoutView-check"
                htmlFor="checkboxAcknowledgment"
              >
                <p className="Passport360EnrollmentCheckoutView-check-info">
                  By clicking “bind coverage”, {quote.brokerage.name}{' '}
                  acknowledges that they have read and agree to the{' '}
                  <a
                    href={`${process.env.PUBLIC_URL}/docs/chubb-consent-to-electronic-delivery.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Electronic Delivery Consent
                  </a>{' '}
                  and{' '}
                  <a
                    href={`${process.env.PUBLIC_URL}/docs/chubb-fraud-warning.html`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    fraud notices
                  </a>
                  .
                </p>
              </label>
            </div>
          )}
        </div>
        {finalizeMethod === 'client_email' ? (
          <button
            type="submit"
            className={classnames('btn btn-large btn-block', {
              'btn-complete': !isInProgress,
              'btn-default': isInProgress,
            })}
            disabled={this.isSubmitDisabled}
          >
            {isInProgress ? (
              <span>
                <i className="fas fa-circle-notch fa-spin" /> Sending proposal
              </span>
            ) : (
              'Send proposal'
            )}
          </button>
        ) : (
          <button
            type="submit"
            className={classnames('btn btn-large btn-block', {
              'btn-complete': !isInProgress,
              'btn-default': isInProgress,
            })}
            disabled={this.isSubmitDisabled}
          >
            {isInProgress ? (
              <span>
                <i className="fas fa-circle-notch fa-spin" /> Binding coverage
              </span>
            ) : (
              'Bind coverage'
            )}
          </button>
        )}
      </Fragment>
    );
  }

  get formNodeClient() {
    const { quote } = this.props;
    const {
      billingType,
      isAcknowledgmentAgreed,
      isAutoRenewSelected,
      isInProgress,
    } = this.state;
    const btnText =
      billingType === 'platform_eft'
        ? 'Pay and bind coverage'
        : 'Bind coverage';
    return (
      <Fragment>
        <div className="form-group">
          <select
            className="form-control"
            name="billingType"
            onChange={this.handleBillingTypeChange}
          >
            <option value="platform_eft">Credit or Debit Card</option>
            <option value="carrier_bill">Check (Bill me later)</option>
          </select>
        </div>
        {billingType === 'platform_eft' && (
          <CreditCardFields
            onChange={this.handleCCFieldChange}
            ref={(c) => (this.creditCardFieldNode = c)}
          />
        )}
        <div className="form-group">
          <div className="form-check">
            <label className="Passport360EnrollmentCheckoutView-check form-check-label">
              <input
                className="form-check-input"
                type="checkbox"
                name="autoRenew"
                value="autoRenew"
                checked={isAutoRenewSelected}
                onChange={this.handleAutoRenewUpdate}
              />
              <p className="Passport360EnrollmentCheckoutView-check-info">
                Automatically renew on
                {moment(quote.application.policy_effective_date)
                  .add(1, 'year')
                  .format(' MM/DD/YYYY ')}
                to prevent a lapse in coverage
              </p>
            </label>
            <label className="Passport360EnrollmentCheckoutView-check form-check-label">
              <input
                className="form-check-input"
                type="checkbox"
                name="acknowledgment"
                value="acknowledgment"
                checked={isAcknowledgmentAgreed}
                onChange={this.handleAcknowledgmentUpdate}
              />
              <p className="Passport360EnrollmentCheckoutView-check-info">
                By clicking “bind coverage”, I acknowledge that I have read and
                agree to the{' '}
                <a
                  href={`${process.env.PUBLIC_URL}/docs/chubb-consent-to-electronic-delivery.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Electronic Delivery Consent
                </a>{' '}
                and{' '}
                <a
                  href={`${process.env.PUBLIC_URL}/docs/chubb-fraud-warning.html`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  fraud notices
                </a>
                .
              </p>
            </label>
          </div>
        </div>
        <button
          type="submit"
          className={classnames('btn btn-large btn-block', {
            'btn-complete': !isInProgress,
            'btn-default': isInProgress,
          })}
          disabled={this.isSubmitDisabled}
        >
          {isInProgress ? (
            <span>
              <i className="fas fa-circle-notch fa-spin" /> Binding coverage
            </span>
          ) : (
            btnText
          )}
        </button>
        {billingType === 'platform_eft' && <CreditCardLogos />}
      </Fragment>
    );
  }

  render() {
    const { actor, isCheckingOutQuote, quote, quoteCheckout } = this.props;
    const currencyOpts = {
      code: get(this.selectedPlan, 'currency'),
    };

    return this.selectedPlan ? (
      <div className="Passport360EnrollmentCheckoutView">
        <div className="row justify-content-center">
          <div className="col-md-8 p-t-30">
            <h3 className="text-center semi-bold">You're almost done</h3>
            <a
              className="Passport360EnrollmentCheckoutView-samplePolicyLink"
              href={this.selectedPlan.settings.sample_policy_url}
              target="_blank"
              rel="noopener noreferrer"
              data-key="checkoutSamplePolicy"
              onClick={this.trackDocClick}
            >
              <i className="Passport360EnrollmentCheckoutView-samplePolicyLink-icon fal fa-file-alt" />
              view sample policy
            </a>
            <div className="row">
              <div className="col-md-6">
                <div className="Passport360EnrollmentCheckoutView-planCard card">
                  <table className="Passport360EnrollmentCheckoutView-table table table-striped">
                    <thead>
                      <tr>
                        <th colSpan="2">
                          Your plan
                          <Link
                            to="plan-type"
                            className="Passport360EnrollmentCheckoutView-editLink"
                          >
                            Edit
                          </Link>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{get(this.selectedPlan, 'settings.plan_title')}</td>
                        <td>
                          {currencyFormat(
                            get(
                              this.selectedPlan,
                              'settings.details.plan_price'
                            ),
                            currencyOpts
                          )}
                        </td>
                      </tr>
                      {get(
                        this.selectedPlan,
                        'settings.details.optional_coverages.golf_package'
                      ) && (
                        <tr>
                          <td>
                            {get(
                              this.selectedPlan,
                              'settings.details.optional_coverages.golf_package.title'
                            )}
                          </td>
                          <td>
                            {currencyFormat(
                              get(
                                this.selectedPlan,
                                'settings.details.optional_coverages.golf_package.price'
                              ),
                              currencyOpts
                            )}
                          </td>
                        </tr>
                      )}
                      {get(
                        this.selectedPlan,
                        'settings.details.optional_coverages.trip_cancellation'
                      ) && (
                        <tr>
                          <td>
                            {get(
                              this.selectedPlan,
                              'settings.details.optional_coverages.trip_cancellation.title'
                            )}
                          </td>
                          <td>
                            {currencyFormat(
                              get(
                                this.selectedPlan,
                                'settings.details.optional_coverages.trip_cancellation.price'
                              ),
                              currencyOpts
                            )}
                          </td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-black">
                          <strong>Total due</strong>
                        </td>
                        <td className="text-black">
                          {!isCheckingOutQuote && (
                            <strong>
                              {currencyFormat(
                                quoteCheckout.grandtotal,
                                currencyOpts
                              )}
                            </strong>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="col-md-6">
                <div className="Passport360EnrollmentCheckoutView-checkoutCard card">
                  <div className="card-block">
                    <h5 className="Passport360EnrollmentCheckoutView-checkoutCard-totalHeading">
                      Total due
                    </h5>
                    <p className="Passport360EnrollmentCheckoutView-checkoutCard-price">
                      {!isCheckingOutQuote && (
                        <span>
                          {currencyFormat(
                            quoteCheckout.grandtotal,
                            currencyOpts
                          )}
                        </span>
                      )}
                    </p>
                    <form onSubmit={this.handleSubmit}>
                      <div className="form-group">
                        <label htmlFor="startDate">Coverage start date</label>
                        <div className="input-group date m-b-20">
                          <span className="input-group-addon" id="basic-addon3">
                            <i className="fa fa-calendar-alt" />
                          </span>
                          <BSDatepicker
                            type="text"
                            className="form-control"
                            id="startDate"
                            placeholder="Pick a date"
                            aria-describedby="startDate"
                            value={
                              moment(
                                quote.application.policy_effective_date
                              ).format('MM/DD/YYYY') || ''
                            }
                            datepickerOpts={{
                              autoclose: true,
                              startDate: moment().toISOString(),
                              endDate: '+89d',
                            }}
                            onChange={this.handleDateChange}
                          />
                        </div>
                      </div>
                      {actor === 'broker'
                        ? this.formNodeBroker
                        : this.formNodeClient}
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : (
      <div />
    );
  }
}

Passport360EnrollmentCheckoutView.propTypes = propTypes;

export default Passport360EnrollmentCheckoutView;
